// ADO #580 — the Code view shows exactly what Run compiles.
//
// Run (VisualFlowBuilder → TerminalPanel's `code`) builds a one-file project
// with assembleProject. The Code view built it with generateCodeWithLineMap,
// the older walker, which chose the entry differently: in Ram's project the
// Code view showed one good main while Run compiled indexOf's body as main
// and a second `void main`. Both now come from singleFileRunSource. The older
// walker is still run for its node → line map (click a node, its lines light
// up), which remapLineMap carries over onto the text Run compiles.
import { assembleProject } from './codeGenerator';

/**
 * The source Run compiles for a one-file project (no multi-file build).
 *
 * @param {{ projectName?: string, flows?: Object[], globalResources?: Object[], sessions?: Object[], files?: Object[] }} state
 * @param {string} language
 * @param {{ isDebugMode?: boolean }} [extra] isDebugMode: the instrumented build a guided tour / lesson run uses
 * @returns {string}
 */
export function singleFileRunSource(state, language, extra = {}) {
  const flows = state.flows || [];
  // ADO #445 — the entry file's sessions, so a Python module holding several
  // top-level classes emits `class X:` blocks.
  let fileSessions = state.sessions || [];
  const entryFlow = flows.find((f) => f.isEntry);
  const entryFile = (state.files || []).find((f) => f.name === entryFlow?.fileName);
  if (entryFile) fileSessions = fileSessions.filter((s) => s.fileId === entryFile.id);
  const opts = fileSessions.length ? { sessions: fileSessions } : {};
  if (extra.isDebugMode) opts.isDebugMode = true;
  return assembleProject(
    { projectName: state.projectName, flows, globalResources: state.globalResources || [] },
    language,
    opts,
  );
}

/**
 * Carry a node → {startLine, endLine} map made against `fromCode` over to
 * `toCode`, by matching their lines (longest common subsequence after the
 * common head and tail). A node none of whose lines survive is dropped.
 *
 * @param {string} fromCode
 * @param {string} toCode
 * @param {Object<string, {startLine:number, endLine:number}>} lineMap
 * @returns {Object<string, {startLine:number, endLine:number}>}
 */
export function remapLineMap(fromCode, toCode, lineMap) {
  if (fromCode === toCode || !lineMap) return lineMap || {};
  const a = fromCode.split('\n');
  const b = toCode.split('\n');
  const at = new Array(a.length).fill(-1); // a index -> b index

  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) { at[head] = head; head += 1; }
  let tail = 0;
  while (tail < a.length - head && tail < b.length - head
    && a[a.length - 1 - tail] === b[b.length - 1 - tail]) {
    at[a.length - 1 - tail] = b.length - 1 - tail;
    tail += 1;
  }

  const A = a.slice(head, a.length - tail);
  const B = b.slice(head, b.length - tail);
  const n = A.length;
  const m = B.length;
  if (n > 0 && m > 0 && n * m <= 4e6) {
    const w = m + 1;
    const dp = new Uint16Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        dp[i * w + j] = A[i] === B[j]
          ? dp[(i + 1) * w + j + 1] + 1
          : Math.max(dp[(i + 1) * w + j], dp[i * w + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (A[i] === B[j]) { at[head + i] = head + j; i += 1; j += 1; }
      else if (dp[(i + 1) * w + j] >= dp[i * w + j + 1]) i += 1;
      else j += 1;
    }
  }

  const out = {};
  Object.entries(lineMap).forEach(([id, r]) => {
    let start = null;
    let end = null;
    for (let line = r.startLine; line <= r.endLine; line += 1) {
      const to = at[line - 1];
      if (to === undefined || to < 0) continue;
      if (start === null) start = to + 1;
      end = to + 1;
    }
    if (start !== null) out[id] = { startLine: start, endLine: end };
  });
  return out;
}
